/**
 * HeadlightToastAdapter
 *
 * Pure, framework-free translation between the legacy object-first toast options
 * (`{ message, type: 'is-danger', ... }`) used across ~45 Headlight call sites and
 * the Pict-Section-Modal `toast(message, options)` API. Kept as a plain class (no
 * Pict/DOM dependencies) so it is unit-testable in isolation.
 *
 * Behaviors preserved from the former bulma-toast wrapper:
 *  - `is-*` type classes mapped to PSM's enum (info/success/warning/error)
 *  - single-toast (only one visible at a time) via handle bookkeeping
 *  - duplicate throttle (identical type+message within a short window is dropped)
 *  - HTML messages rendered (auto-detected, or forced via `allowHTML`)
 *
 * Added since: `sticky` toasts, which opt out of auto-dismiss, the throttle, and single-toast eviction.
 */

const _TYPE_MAP =
{
	'is-info': 'info',
	'is-success': 'success',
	'is-warning': 'warning',
	'is-danger': 'error',
	'is-error': 'error',
	// pass-through for callers already using PSM enum values
	'info': 'info',
	'success': 'success',
	'warning': 'warning',
	'error': 'error',
};

class HeadlightToastAdapter
{
	constructor(pOptions)
	{
		this._throttleWindowMS = (pOptions && typeof pOptions.ThrottleWindowMS === 'number') ? pOptions.ThrottleWindowMS : 1000;
		this._recent = {};          // key -> last-shown epoch ms
		this._activeHandles = [];   // outstanding PSM handles (single-toast bookkeeping)
	}

	mapType(pType)
	{
		return _TYPE_MAP[pType] || 'info';
	}

	looksLikeHTML(pMessage)
	{
		// Accept both primitive strings and boxed String objects (typeof 'object').
		if (typeof pMessage !== 'string' && !(pMessage instanceof String))
		{
			return false;
		}
		// RegExp.test coerces a boxed String to its primitive value.
		return /<[a-z][\s\S]*>/i.test(pMessage);
	}

	/**
	 * Translate legacy options into a PSM toast call.
	 * @param {object} pOptions - Legacy toast options ({ message, type, duration, position, dismissible, allowHTML, sticky }).
	 * @param {number} pNowMS - Current epoch ms (injected for testability).
	 * @returns {?{ message: string, sticky: boolean, options: object }} null when throttled or invalid.
	 */
	translate(pOptions, pNowMS)
	{
		if (!pOptions || !pOptions.message)
		{
			return null;
		}

		let tmpType = this.mapType(pOptions.type);
		const tmpSticky = (pOptions.sticky === true);

		// A sticky toast is a state indicator, not a notification: its owner locks the UI on the
		// assumption it is showing, so the duplicate throttle must not be able to swallow it.
		if (!tmpSticky)
		{
			const tmpKey = tmpType + '::' + pOptions.message;

			if (this._recent[tmpKey] && (pNowMS - this._recent[tmpKey]) < this._throttleWindowMS)
			{
				return null;
			}
			this._recent[tmpKey] = pNowMS;
		}

		let tmpAllowHTML = (typeof pOptions.allowHTML === 'boolean') ? pOptions.allowHTML : this.looksLikeHTML(pOptions.message);

		return (
			{
				message: pOptions.message,
				sticky: tmpSticky,
				options:
				{
					type: tmpType,
					duration: tmpSticky ? 0 : ((typeof pOptions.duration === 'number') ? pOptions.duration : 3000),
					position: pOptions.position || 'bottom-center',
					dismissible: (typeof pOptions.dismissible === 'boolean') ? pOptions.dismissible : true,
					allowHTML: tmpAllowHTML,
				},
			});
	}

	/**
	 * Single-toast bookkeeping: dismiss any outstanding non-sticky toast(s), then remember the new handle.
	 * Call with `null` to just clear the prior toast (before showing a new one), then again with
	 * the new handle to track it.
	 *
	 * Sticky handles survive this eviction. A sticky toast explains a UI the user cannot interact with,
	 * so the transient toasts that fire *during* that state must not take the explanation away.
	 * @param {?{ dismiss: function }} pHandle
	 * @param {boolean} [pSticky] - Exempt this handle from eviction until releaseHandle takes it back.
	 */
	trackHandle(pHandle, pSticky)
	{
		const tmpRetained = [];
		while (this._activeHandles.length > 0)
		{
			const tmpPrev = this._activeHandles.pop();
			if (!tmpPrev)
			{
				continue;
			}
			if (tmpPrev.Sticky)
			{
				tmpRetained.push(tmpPrev);
				continue;
			}
			if (tmpPrev.Handle && typeof tmpPrev.Handle.dismiss === 'function')
			{
				tmpPrev.Handle.dismiss();
			}
		}
		this._activeHandles = tmpRetained;
		if (pHandle)
		{
			this._activeHandles.push({ Handle: pHandle, Sticky: Boolean(pSticky) });
		}
	}

	/**
	 * Dismiss a specific handle and stop tracking it. Sticky handles are never evicted automatically,
	 * so their owner hands them back here when the state they describe ends.
	 * @param {?{ dismiss: function }} pHandle
	 */
	releaseHandle(pHandle)
	{
		if (!pHandle)
		{
			return;
		}
		this._activeHandles = this._activeHandles.filter((pEntry) => pEntry.Handle !== pHandle);
		if (typeof pHandle.dismiss === 'function')
		{
			pHandle.dismiss();
		}
	}
}

module.exports = HeadlightToastAdapter;
